import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { API_BASE_URL } from "../config/api";
import {
  faUser,
  faChalkboardUser,
  faEnvelope,
  faBars,
  faTimes,
  faInfoCircle,
  faBookOpen,
  faHouse,
  faChevronDown,
  faBullseye,
  faAward,
  faRocket,
  faFolderOpen,
  faAddressBook,
  faArrowRight,
  faLayerGroup,
  faFire,
  faGraduationCap
} from "@fortawesome/free-solid-svg-icons";

function Navbar(props) {
  const value = props.page;
  const navigate = useNavigate();
  const authToken = localStorage.getItem("token");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const dropdownRef = useRef(null);
  const courseDropdownRef = useRef(null);

  const handleLogOut = () => {
    localStorage.clear();
    navigate("/");
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
    setIsCourseDropdownOpen(false);
  };

  // Fetch all courses for total count and catalog link
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/courses`)
      .then((res) => res.json())
      .then((data) => setAllCourses(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Fetch all categories created in the background / admin dashboard
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/categories`)
      .then((res) => res.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Partition categories: Column 1: Trending Categories (Admin marked) | Column 2 & 3: Other Categories
  const trendingCategories = categories.filter((cat) => cat.is_trending === 1 || Number(cat.is_trending) === 1);
  const displayTrending = trendingCategories.length > 0 ? trendingCategories : categories.slice(0, 3);

  const nonTrendingCategories = categories.filter(
    (cat) => !displayTrending.some((tc) => Number(tc.id) === Number(cat.id))
  );

  const halfIndex = Math.ceil(nonTrendingCategories.length / 2);
  const col2Categories = nonTrendingCategories.length > 0
    ? nonTrendingCategories.slice(0, halfIndex)
    : categories.slice(0, 3);
  const col3Categories = nonTrendingCategories.length > 0
    ? nonTrendingCategories.slice(halfIndex)
    : categories.slice(3, 6);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(event.target)) {
        setIsCourseDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isMoreActive = value === "get-started" || value === "resources";

  return (
    <header>
      {/* Top Contact Info Bar */}
      <div className="top-contact-bar">
        <div className="top-contact-info">
          <div className="top-contact-item">
            <FontAwesomeIcon icon={faEnvelope} />
            <a href="mailto:info@consortiumelearning.com">info@consortiumelearning.com</a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav>
        {/* Brand Image Logo & Text — using old_logo.png exclusively for navbar */}
        <Link to="/" className="brand-logo-container" onClick={closeMobileMenu}>
          <div className="brand-logo-badge">
            <img
              src="/old_logo.png"
              alt="Consortium eLearning Academy"
              className="brand-logo-img"
            />
          </div>
          <div className="brand-text">
            <span className="brand-title">Consortium</span>
            <span className="brand-subtitle">eLearning Academy</span>
          </div>
        </Link>

        {/* Navigation Items */}
        <div className="navigation">
          <div id="menu-btn" onClick={toggleMobileMenu}>
            <FontAwesomeIcon icon={faBars} />
          </div>

          {/* Mobile overlay backdrop */}
          {isMobileMenuOpen && (
            <div className="mobile-overlay" onClick={closeMobileMenu}></div>
          )}

          <ul className={isMobileMenuOpen ? "mobile-open" : ""}>
            {/* Mobile close button */}
            {isMobileMenuOpen && (
              <button className="mobile-close-btn" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}

            <li className={value === "home" ? "nav-active" : ""}>
              <Link to="/" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faHouse} /> Home
              </Link>
            </li>

            {/* Desktop Courses Dropdown with 3 Side-by-Side Columns: Trending Categories | Categories | Categories */}
            {!isMobileMenuOpen && (
              <li
                className={`nav-dropdown mega-courses-nav-item ${isCourseDropdownOpen ? "open" : ""} ${value === "courses" ? "nav-active" : ""}`}
                ref={courseDropdownRef}
                onMouseEnter={() => setIsCourseDropdownOpen(true)}
                onMouseLeave={() => setIsCourseDropdownOpen(false)}
              >
                <button
                  className="nav-dropdown-trigger"
                  onClick={() => { navigate("/courses"); setIsCourseDropdownOpen(false); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate("/courses");
                    }
                  }}
                  aria-expanded={isCourseDropdownOpen}
                  aria-haspopup="true"
                  type="button"
                >
                  <FontAwesomeIcon icon={faBookOpen} />
                  <span>Courses</span>
                  <FontAwesomeIcon icon={faChevronDown} className="dropdown-arrow" />
                </button>

                {/* 3-Column Categories Mega Dropdown */}
                <div className="nav-dropdown-menu categories-dropdown-menu">
                  <div className="categories-dropdown-header">
                    <div className="categories-header-title">
                      <FontAwesomeIcon icon={faLayerGroup} style={{ color: "#2563eb" }} />
                      <span>Browse Course Disciplines</span>
                    </div>
                    <span className="categories-header-count">
                      {categories.length} {categories.length === 1 ? "Discipline" : "Disciplines"}
                    </span>
                  </div>

                  <div className="categories-dropdown-grid-3col">
                    {/* Column 1: Trending Categories */}
                    <div className="cat-dropdown-col">
                      <div className="cat-col-header" style={{ color: "#ea580c" }}>
                        <FontAwesomeIcon icon={faFire} />
                        <span>Trending Categories</span>
                      </div>
                      <div className="cat-col-items">
                        {displayTrending.length > 0 ? (
                          displayTrending.map((cat) => (
                            <Link
                              key={`trend-cat-${cat.id}`}
                              to={`/courses?category=${encodeURIComponent(cat.name)}`}
                              className="nav-category-card trending-cat-card"
                              onClick={closeMobileMenu}
                            >
                              <div className="nav-cat-icon trend-icon">
                                <FontAwesomeIcon icon={faFire} />
                              </div>
                              <div className="nav-cat-content">
                                <div className="nav-cat-title" title={cat.name}>{cat.name}</div>
                                <div className="nav-cat-meta">
                                  {cat.course_count > 0 ? `${cat.course_count} ${cat.course_count === 1 ? 'course' : 'courses'}` : 'Trending'}
                                </div>
                              </div>
                              <FontAwesomeIcon icon={faArrowRight} className="nav-cat-arrow" />
                            </Link>
                          ))
                        ) : (
                          <div className="mega-empty-msg">No trending categories marked</div>
                        )}
                      </div>
                    </div>

                    {/* Column 2: Categories (Batch 1) */}
                    <div className="cat-dropdown-col">
                      <div className="cat-col-header" style={{ color: "#2563eb" }}>
                        <FontAwesomeIcon icon={faFolderOpen} />
                        <span>Categories</span>
                      </div>
                      <div className="cat-col-items">
                        {col2Categories.length > 0 ? (
                          col2Categories.map((cat) => (
                            <Link
                              key={`col2-cat-${cat.id}`}
                              to={`/courses?category=${encodeURIComponent(cat.name)}`}
                              className="nav-category-card"
                              onClick={closeMobileMenu}
                            >
                              <div className="nav-cat-icon">
                                <FontAwesomeIcon icon={faFolderOpen} />
                              </div>
                              <div className="nav-cat-content">
                                <div className="nav-cat-title" title={cat.name}>{cat.name}</div>
                                <div className="nav-cat-meta">
                                  {cat.course_count > 0 ? `${cat.course_count} ${cat.course_count === 1 ? 'course' : 'courses'}` : 'Explore'}
                                </div>
                              </div>
                              <FontAwesomeIcon icon={faArrowRight} className="nav-cat-arrow" />
                            </Link>
                          ))
                        ) : (
                          <div className="mega-empty-msg">Explore our catalog</div>
                        )}
                      </div>
                    </div>

                    {/* Column 3: Categories (Batch 2) */}
                    <div className="cat-dropdown-col">
                      <div className="cat-col-header" style={{ color: "#059669" }}>
                        <FontAwesomeIcon icon={faGraduationCap} />
                        <span>More Categories</span>
                      </div>
                      <div className="cat-col-items">
                        {col3Categories.length > 0 ? (
                          col3Categories.map((cat) => (
                            <Link
                              key={`col3-cat-${cat.id}`}
                              to={`/courses?category=${encodeURIComponent(cat.name)}`}
                              className="nav-category-card"
                              onClick={closeMobileMenu}
                            >
                              <div className="nav-cat-icon green-icon">
                                <FontAwesomeIcon icon={faGraduationCap} />
                              </div>
                              <div className="nav-cat-content">
                                <div className="nav-cat-title" title={cat.name}>{cat.name}</div>
                                <div className="nav-cat-meta">
                                  {cat.course_count > 0 ? `${cat.course_count} ${cat.course_count === 1 ? 'course' : 'courses'}` : 'Explore'}
                                </div>
                              </div>
                              <FontAwesomeIcon icon={faArrowRight} className="nav-cat-arrow" />
                            </Link>
                          ))
                        ) : (
                          <div className="mega-empty-msg">More modules arriving</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Dropdown Footer Bar */}
                  <div className="categories-dropdown-footer">
                    <span className="categories-footer-count">
                      🎓 <strong>{allCourses.length}</strong> accredited programs in catalog
                    </span>
                    <Link
                      to="/courses"
                      className="categories-footer-viewall"
                      onClick={closeMobileMenu}
                    >
                      <span>Explore All Courses</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                  </div>
                </div>
              </li>
            )}

            {/* Mobile-only: simple courses link */}
            {isMobileMenuOpen && (
              <li className={value === "courses" ? "nav-active" : ""}>
                <Link to="/courses" onClick={closeMobileMenu}>
                  <FontAwesomeIcon icon={faBookOpen} /> Courses
                </Link>
              </li>
            )}

            <li className={value === "goals" ? "nav-active" : ""}>
              <Link to="/goals" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faBullseye} /> Goals
              </Link>
            </li>
            <li className={value === "accreditations" ? "nav-active" : ""}>
              <Link to="/accreditations" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faAward} /> Accreditations
              </Link>
            </li>
            <li className={value === "about" ? "nav-active" : ""}>
              <Link to="/about" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faInfoCircle} /> About
              </Link>
            </li>
            <li className={value === "contact" ? "nav-active" : ""}>
              <Link to="/contact" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faAddressBook} /> Contact
              </Link>
            </li>

            {/* Desktop 'More' Dropdown with perfect alignment */}
            {!isMobileMenuOpen && (
              <li
                className={`nav-dropdown ${isDropdownOpen ? "open" : ""} ${isMoreActive ? "nav-active" : ""}`}
                ref={dropdownRef}
                onMouseEnter={() => setIsDropdownOpen(true)}
                onMouseLeave={() => setIsDropdownOpen(false)}
              >
                <button
                  className="nav-dropdown-trigger"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsDropdownOpen((prev) => !prev);
                    }
                  }}
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                  type="button"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  <span>More</span>
                  <FontAwesomeIcon icon={faChevronDown} className="dropdown-arrow" />
                </button>
                <div className="nav-dropdown-menu">
                  <Link 
                    to="/get-started" 
                    className={value === "get-started" ? "dropdown-item active" : "dropdown-item"}
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <FontAwesomeIcon icon={faRocket} className="dropdown-item-icon" />
                    <span>Get Started</span>
                  </Link>
                  <Link 
                    to="/resources" 
                    className={value === "resources" ? "dropdown-item active" : "dropdown-item"}
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <FontAwesomeIcon icon={faFolderOpen} className="dropdown-item-icon" />
                    <span>Resources & Policies</span>
                  </Link>
                </div>
              </li>
            )}

            {/* Mobile-only view for More pages */}
            {isMobileMenuOpen && (
              <>
                <li className={value === "get-started" ? "nav-active" : ""}>
                  <Link to="/get-started" onClick={closeMobileMenu}>
                    <FontAwesomeIcon icon={faRocket} /> Get Started
                  </Link>
                </li>
                <li className={value === "resources" ? "nav-active" : ""}>
                  <Link to="/resources" onClick={closeMobileMenu}>
                    <FontAwesomeIcon icon={faFolderOpen} /> Resources & Policies
                  </Link>
                </li>
              </>
            )}

            {authToken && (
              <>
                <li className={value === "profile" ? "nav-active" : ""}>
                  <Link to="/profile" onClick={closeMobileMenu}>
                    <FontAwesomeIcon icon={faUser} /> Profile
                  </Link>
                </li>
                <li className={value === "learnings" ? "nav-active" : ""}>
                  <Link to="/learnings" onClick={closeMobileMenu}>
                    <FontAwesomeIcon icon={faChalkboardUser} /> My Learnings
                  </Link>
                </li>
              </>
            )}

            {authToken ? (
              <li>
                <button onClick={() => { handleLogOut(); closeMobileMenu(); }} className="sign-out-btn">
                  Sign Out
                </button>
              </li>
            ) : (
              <li>
                <button onClick={() => { navigate("/login"); closeMobileMenu(); }} className="nav-btn">
                  Login / Sign Up
                </button>
              </li>
            )}
          </ul>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;
